import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { api, formatCurrency, formatDate, formatDateTime, formatStatus } from '../../utils/api'
import { PRODUCT_TYPES } from '../../utils/constants'
import { MetricsRow, MultiSelectDropdown, ProductDetailModal, OrderDetailModal, FulfillmentBadge, FinancialBadge, HoldBadge } from '../shared'

// The value of the status filter's "On hold" option.
const ON_HOLD_FILTER = 'on_hold'

function OrdersList() {
  const location = useLocation()
  const navigate = useNavigate()

  const [allOrders, setAllOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [marketplaces, setMarketplaces] = useState([])
  const [collections, setCollections] = useState([])
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [loadingProduct, setLoadingProduct] = useState(false)
  const [loadingOrder, setLoadingOrder] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [totalCount, setTotalCount] = useState(0)

  const [searchFilters, setSearchFilters] = useState({
    order_id: '',
    email: '',
    tracking_number: '',
    postcode: '',
    marketplace: '',
    date_from: '',
    date_to: '',
    product_id: '',
    sku: '',
    title: '',
    tags: '',
    product_types: [],
    price_min: '',
    price_max: '',
    status: 'unfulfilled'
  })

  // Display options (client-side)
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const [limit, setLimit] = useState(25)
  const [page, setPage] = useState(1)

  // Track if we have URL params to search for
  const [urlParamsLoaded, setUrlParamsLoaded] = useState(false)

  // Read URL parameters on initial load and populate search filters
  useEffect(() => {
    loadReferenceData()

    const params = new URLSearchParams(window.location.search)
    const orderIdParam = params.get('order_id')
    const emailParam = params.get('email')
    const statusParam = params.get('status')

    // If URL has search params, update filters and search
    if (orderIdParam || emailParam) {
      const newFilters = { ...searchFilters }
      if (orderIdParam) newFilters.order_id = orderIdParam
      if (emailParam) newFilters.email = emailParam
      // A deep link to one order has to find it whatever its status, so the
      // default Unfulfilled filter is cleared. An explicit ?status= in the
      // link is a deliberate instruction and wins.
      //
      // These were two lines: one read statusParam into the filters, the next
      // overwrote it with '' unconditionally. The parameter was read, parsed
      // and discarded, so a link carrying it behaved exactly like a link
      // without it while looking like it was supported.
      newFilters.status = statusParam || ''

      setSearchFilters(newFilters)
      setUrlParamsLoaded(true)
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname)
    } else {
      loadOrders()
    }
  }, [])

  // Trigger search when URL params are loaded into filters
  useEffect(() => {
    if (urlParamsLoaded) {
      loadOrders()
      setUrlParamsLoaded(false)
    }
  }, [urlParamsLoaded])

  // Watch for orderId in URL (for pinned items navigation)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const orderIdParam = params.get('orderId')
    if (orderIdParam) {
      loadAndOpenOrder(orderIdParam)
      navigate(location.pathname, { replace: true })
    }
  }, [location.key])

  async function loadAndOpenOrder(orderId) {
    try {
      setLoadingOrder(true)
      const cleanId = String(orderId).replace(/^#/, '')
      const isReverb = cleanId.startsWith('reverb_')
      const isEbay = cleanId.startsWith('ebay_')
      let searchParam
      if (isReverb) {
        searchParam = `marketplace=reverb&order_id=${cleanId.replace('reverb_', '')}&status=`
      } else if (isEbay) {
        searchParam = `marketplace=ebay&order_id=${cleanId.replace('ebay_', '')}&status=`
      } else {
        searchParam = `order_id=${cleanId}&status=`
      }
      const response = await api.get(`/api/orders/search?${searchParam}`)
      const orders = response.orders || []
      if (orders.length > 0) {
        setSelectedOrder(orders[0])
      } else {
        console.warn('Order not found:', cleanId)
      }
    } catch (error) {
      console.error('Error loading order:', error)
    } finally {
      setLoadingOrder(false)
    }
  }

  async function loadReferenceData() {
    try {
      const [marketplacesRes, collectionsRes] = await Promise.all([
        api.get('/api/orders/marketplaces'),
        api.get('/api/inventory/shopify/collections')
      ])
      setMarketplaces(marketplacesRes.marketplaces || [])
      setCollections(collectionsRes.collections || [])
    } catch (err) {
      console.error('Failed to load reference data:', err)
    }
  }

  const loadOrders = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (searchFilters.order_id) params.set('order_id', searchFilters.order_id)
      if (searchFilters.email) params.set('email', searchFilters.email)
      if (searchFilters.tracking_number) params.set('tracking_number', searchFilters.tracking_number)
      if (searchFilters.postcode) params.set('postcode', searchFilters.postcode)
      if (searchFilters.marketplace) params.set('marketplace', searchFilters.marketplace)
      if (searchFilters.date_from) params.set('date_from', searchFilters.date_from)
      if (searchFilters.date_to) params.set('date_to', searchFilters.date_to)
      if (searchFilters.product_id) params.set('product_id', searchFilters.product_id)
      if (searchFilters.sku) params.set('sku', searchFilters.sku)
      if (searchFilters.title) params.set('title', searchFilters.title)
      if (searchFilters.tags) params.set('tags', searchFilters.tags)
      if (searchFilters.product_types.length > 0) params.set('product_types', searchFilters.product_types.join(','))
      if (searchFilters.price_min) params.set('price_min', searchFilters.price_min)
      if (searchFilters.price_max) params.set('price_max', searchFilters.price_max)
      // "On hold" is not a fulfilment status: Prism filters held orders on their own flag.
      if (searchFilters.status === ON_HOLD_FILTER) params.set('on_hold', 'true')
      else if (searchFilters.status) params.set('status', searchFilters.status)
      params.set('limit', '200')

      const response = await api.get(`/api/orders/search?${params}`)
      setAllOrders(response.orders || [])
      setTotalCount(response.count || 0)
    } catch (err) {
      console.error('Failed to load orders:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [searchFilters])

  function handleFilterChange(field, value) {
    setSearchFilters(prev => ({ ...prev, [field]: value }))
  }

  function handleSearch(e) {
    e.preventDefault()
    loadOrders()
  }

  function clearFilters() {
    setSearchFilters({
      order_id: '',
      email: '',
      tracking_number: '',
      postcode: '',
      marketplace: '',
      date_from: '',
      date_to: '',
      product_id: '',
      sku: '',
      title: '',
      tags: '',
      product_types: [],
      price_min: '',
      price_max: '',
      status: 'unfulfilled'
    })
    setSortBy('created_at')
    setSortOrder('desc')
    setLimit(25)
    setPage(1)
  }

  // Client-side sorting and pagination
  const { displayOrders, totalPages } = useMemo(() => {
    const sorted = [...allOrders].sort((a, b) => {
      let aVal = a[sortBy]
      let bVal = b[sortBy]

      if (['total_price'].includes(sortBy)) {
        aVal = parseFloat(aVal) || 0
        bVal = parseFloat(bVal) || 0
      }

      if (typeof aVal === 'string') aVal = aVal.toLowerCase()
      if (typeof bVal === 'string') bVal = bVal.toLowerCase()

      if (['created_at', 'updated_at'].includes(sortBy)) {
        aVal = new Date(aVal).getTime()
        bVal = new Date(bVal).getTime()
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    const totalPages = Math.ceil(sorted.length / limit)
    const start = (page - 1) * limit
    const orders = sorted.slice(start, start + limit)
    return { displayOrders: orders, totalPages }
  }, [allOrders, sortBy, sortOrder, limit, page])

  function handleSort(field) {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  function SortHeader({ field, children, align, className = '' }) {
    const isActive = sortBy === field
    const arrow = isActive ? (sortOrder === 'asc' ? ' \u25B2' : ' \u25BC') : ''
    return (
      <th
        onClick={() => handleSort(field)}
        style={{ cursor: 'pointer', textAlign: align, userSelect: 'none' }}
        className={`${isActive ? 'sort-active' : ''} ${className}`}
      >
        {children}{arrow}
      </th>
    )
  }

  function handleRowClick(order) {
    setSelectedOrder(order)
  }

  async function handleProductClick(productId) {
    setLoadingProduct(true)
    try {
      const response = await api.get(`/api/inventory/shopify/product/${productId}`)
      setSelectedProduct(response.product)
    } catch (err) {
      console.error('Failed to load product:', err)
      alert('Failed to load product details')
    } finally {
      setLoadingProduct(false)
    }
  }

  async function handleProductSave(productId, updates) {
    try {
      await api.put(`/api/inventory/shopify/product/${productId}`, updates)
      const response = await api.get(`/api/inventory/shopify/product/${productId}`)
      setSelectedProduct(response.product)
    } catch (err) {
      console.error('Failed to save product:', err)
      throw err
    }
  }

  // Check if advanced filters are active
  const hasAdvancedFilters = searchFilters.product_id || searchFilters.sku || searchFilters.title ||
    searchFilters.tags || searchFilters.product_types.length > 0 ||
    searchFilters.price_min || searchFilters.price_max

  // Calculate metrics over allOrders, the whole result set, not displayOrders,
  // which is only the page currently on screen. These sat next to a Loaded tile
  // reading allOrders.length, so a 44-order queue viewed at 25 per page showed
  // '44 Loaded' beside '19 Unfulfilled' and a Total Revenue covering just the
  // 19 rows of page 2. Pressing Next silently restated all three.
  const totalRevenue = allOrders.reduce((sum, order) => sum + (parseFloat(order.total_price) || 0), 0)
  const avgOrderValue = allOrders.length > 0 ? totalRevenue / allOrders.length : 0
  const unfulfilledCount = allOrders.filter(o => !o.cancelled_at && o.status !== 'cancelled' && (!o.fulfillment_status || o.fulfillment_status === 'unfulfilled')).length

  const metrics = [
    { label: 'Loaded', value: allOrders.length, color: 'blue' },
    { label: 'Total Revenue', value: totalRevenue, format: 'currency', color: 'green' },
    { label: 'Avg Order Value', value: avgOrderValue, format: 'currency', color: 'purple' },
    { label: 'Unfulfilled', value: unfulfilledCount, color: 'amber' }
  ]

  const productTypeOptions = PRODUCT_TYPES.map(t => ({ value: t, label: t }))

  return (
    <>
      <div style={{ marginTop: 28 }}>
        <MetricsRow metrics={metrics} />
      </div>

      <form onSubmit={handleSearch}>
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          {/* Main Filters */}
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <div className="filter-group" style={{ width: 200 }}>
              <label className="filter-label">Order ID / Number</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. 5432109876543 or AT-GB-1234"
                value={searchFilters.order_id}
                onChange={e => handleFilterChange('order_id', e.target.value)}
              />
            </div>
            <div className="filter-group" style={{ flex: 1, minWidth: 250 }}>
              <label className="filter-label">Email</label>
              <input
                type="text"
                className="form-input"
                placeholder="customer@example.com"
                value={searchFilters.email}
                onChange={e => handleFilterChange('email', e.target.value)}
              />
            </div>
            <div className="filter-group" style={{ width: 175 }}>
              <label className="filter-label" htmlFor="orders-tracking-filter">Tracking number</label>
              <input
                id="orders-tracking-filter"
                type="text"
                className="form-input"
                placeholder="e.g. JD0146..."
                value={searchFilters.tracking_number}
                onChange={e => handleFilterChange('tracking_number', e.target.value)}
              />
            </div>
            <div className="filter-group" style={{ width: 140 }}>
              <label className="filter-label" htmlFor="orders-postcode-filter">Postcode</label>
              <input
                id="orders-postcode-filter"
                type="text"
                className="form-input"
                placeholder="e.g. TR1 1TH"
                value={searchFilters.postcode}
                onChange={e => handleFilterChange('postcode', e.target.value)}
              />
            </div>
            <div className="filter-group" style={{ width: 175 }}>
              <label className="filter-label">Marketplace</label>
              <select
                className="form-input"
                value={searchFilters.marketplace}
                onChange={e => handleFilterChange('marketplace', e.target.value)}
              >
                <option value="">All</option>
                {marketplaces.map(m => (
                  <option key={m.name} value={m.name}>{m.displayName}</option>
                ))}
              </select>
            </div>
            <div className="filter-group" style={{ width: 160 }}>
              <label className="filter-label" htmlFor="orders-status-filter">Fulfillment Status</label>
              <select
                id="orders-status-filter"
                className="form-input"
                value={searchFilters.status}
                onChange={e => handleFilterChange('status', e.target.value)}
              >
                <option value="">All statuses</option>
                <option value="unfulfilled">Unfulfilled</option>
                <option value="fulfilled">Fulfilled</option>
                <option value="partial">Partial</option>
                <option value="refunded">Refunded</option>
                <option value={ON_HOLD_FILTER}>On hold</option>
              </select>
            </div>
            <div className="filter-group" style={{ width: 140 }}>
              <label className="filter-label">Date From</label>
              <input
                type="date"
                className="form-input"
                value={searchFilters.date_from}
                onChange={e => handleFilterChange('date_from', e.target.value)}
              />
            </div>
            <div className="filter-group" style={{ width: 140 }}>
              <label className="filter-label">to</label>
              <input
                type="date"
                className="form-input"
                value={searchFilters.date_to}
                onChange={e => handleFilterChange('date_to', e.target.value)}
              />
            </div>
          </div>

          {/* Advanced Toggle */}
          <button
            type="button"
            className="filter-toggle"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <span className={`filter-toggle-icon ${showAdvanced ? 'expanded' : ''}`}>&#9660;</span>
            {showAdvanced ? 'Hide' : 'Show'} Advanced Search
            {hasAdvancedFilters && <span className="filter-toggle-active">(filters active)</span>}
          </button>

          {/* Advanced Filters */}
          {showAdvanced && (
            <div className="advanced-filters">
              <div className="filter-section">
                <div className="filter-group">
                  <label className="filter-label">Product ID</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. 8234567890123"
                    value={searchFilters.product_id}
                    onChange={e => handleFilterChange('product_id', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">SKU</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search by SKU..."
                    value={searchFilters.sku}
                    onChange={e => handleFilterChange('sku', e.target.value)}
                  />
                </div>
                <div className="filter-group filter-group-wide">
                  <label className="filter-label">Product Title</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search by title..."
                    value={searchFilters.title}
                    onChange={e => handleFilterChange('title', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Product Tags</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="tag1, tag2..."
                    value={searchFilters.tags}
                    onChange={e => handleFilterChange('tags', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Product Type</label>
                  <MultiSelectDropdown
                    options={productTypeOptions}
                    selected={searchFilters.product_types}
                    onChange={values => handleFilterChange('product_types', values)}
                    placeholder="All types"
                  />
                </div>
              </div>
              <div className="filter-section">
                <div className="filter-group">
                  <label className="filter-label">Sale Price Min</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="0.00"
                    value={searchFilters.price_min}
                    onChange={e => handleFilterChange('price_min', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Sale Price Max</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="No limit"
                    value={searchFilters.price_max}
                    onChange={e => handleFilterChange('price_max', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="filter-actions" style={{ justifyContent: 'flex-start', gap: 'var(--space-3)' }}>
            <button type="button" className="btn btn-secondary" onClick={clearFilters}>
              Clear
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Loading...' : 'Search'}
            </button>
            {allOrders.length > 0 && (
              <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
                Showing {displayOrders.length} of {allOrders.length} loaded ({totalCount} total)
              </span>
            )}
          </div>
        </div>
      </form>

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : displayOrders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Orders Found</div>
            <div className="empty-state-description">Try adjusting your search filters.</div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <SortHeader field="name">Order</SortHeader>
                <th className="col-mobile-hide">Customer</th>
                <th className="col-mobile-hide">Items</th>
                <SortHeader field="source_name" className="col-mobile-hide">Channel</SortHeader>
                <th>Status</th>
                <th className="col-mobile-hide">Payment</th>
                <SortHeader field="total_price" align="right" className="col-mobile-hide">Total</SortHeader>
                <SortHeader field="created_at" className="col-mobile-hide">Date</SortHeader>
              </tr>
            </thead>
            <tbody>
              {displayOrders.map(order => (
                <tr
                  key={order.id}
                  onClick={() => handleRowClick(order)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="col-mobile-primary">
                    <div style={{ fontWeight: 500 }}>{order.name}</div>
                    <div className="text-sm text-muted">#{order.order_number}</div>
                    <div className="mobile-row-meta" style={{ display: 'none' }}>
                      {order.customer && <span>{order.customer.first_name} {order.customer.last_name}</span>}
                      <span>{order.line_items?.length || order.line_items_count || 0} items</span>
                    </div>
                  </td>
                  <td className="col-mobile-hide">
                    {order.customer ? (
                      <>
                        <div>{order.customer.first_name} {order.customer.last_name}</div>
                        <div className="text-sm text-muted">{order.customer.email}</div>
                      </>
                    ) : (
                      <span className="text-muted">Guest</span>
                    )}
                  </td>
                  <td className="col-mobile-hide">
                    <div style={{ maxWidth: 200 }}>
                      {order.line_items?.length > 0 ? (
                        <>
                          {order.line_items.slice(0, 2).map((item, i) => (
                            <div key={i} className="text-sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.quantity}x {item.title}
                            </div>
                          ))}
                          {order.line_items.length > 2 && (
                            <span className="text-sm text-muted">+{order.line_items.length - 2} more</span>
                          )}
                        </>
                      ) : order.line_items_summary ? (
                        <div className="text-sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {order.line_items_count > 1 && <>{order.line_items_count}x </>}{order.line_items_summary}
                        </div>
                      ) : (
                        <span className="text-sm text-muted">{order.line_items_count || 0} items</span>
                      )}
                    </div>
                  </td>
                  <td className="text-sm col-mobile-hide">{order.source_name || 'web'}</td>
                  <td>
                    <FulfillmentBadge status={(order.cancelled_at || order.status === 'cancelled') ? 'cancelled' : order.fulfillment_status} />
                    {order.on_hold && <> <HoldBadge hold={order.on_hold} /></>}
                  </td>
                  <td className="col-mobile-hide"><FinancialBadge status={order.financial_status} /></td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }} className="col-mobile-hide">
                    {formatCurrency(order.total_price)}
                    {order.currency !== 'GBP' && <span className="text-sm text-muted"> {order.currency}</span>}
                  </td>
                  <td className="text-sm text-muted col-mobile-hide">{formatDate(order.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {displayOrders.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
            <span className="text-sm text-muted">
              Showing {(page - 1) * limit + 1}-{Math.min(page * limit, allOrders.length)} of {allOrders.length}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              {/* Page navigation */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Back
                </button>
                <span className="text-sm" style={{ minWidth: 80, textAlign: 'center' }}>
                  Page {page} of {totalPages || 1}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </button>
              </div>
              {/* Per page selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', borderLeft: '1px solid var(--color-border)', paddingLeft: 'var(--space-3)' }}>
                <span className="text-sm text-muted">Per page:</span>
                {[25, 50, 100].map(num => (
                  <button
                    key={num}
                    type="button"
                    className={`btn btn-sm ${limit === num ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setLimit(num); setPage(1); }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* onOrderUpdate reloads with the filters untouched. It used to clear the
          status filter so a just-shipped order stayed on screen, but shipping
          staff work the Unfulfilled list as a queue: the order they just
          fulfilled should drop out of it and the rest of the queue should still
          be there. Losing the filter after every order meant re-selecting
          Unfulfilled all day (ticket #1080, raised for Veni). */}
      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onProductClick={handleProductClick}
          loadingProduct={loadingProduct}
          onOrderUpdate={() => loadOrders()}
        />
      )}

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          collections={collections}
          onClose={() => setSelectedProduct(null)}
          onSave={handleProductSave}
        />
      )}
    </>
  )
}

export default OrdersList
